import Seo from "../components/Seo";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LEGAL_CONTENT } from "../lib/legalContent";
import { LEGAL_UPDATED_AT } from "../lib/siteConfig";
import { openCookieSettings } from "../lib/consent";
import { safeHtml } from "../lib/sanitize";

const PATHS = { mentions: "/mentions-legales", cgv: "/cgv", confidentialite: "/confidentialite" };

const slug = (s) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/**
 * Pages légales : Mentions légales, CGV, Politique de confidentialité.
 * Le contenu vient de lib/legalContent.js ; les informations manquantes s'affichent « à compléter ».
 */
export default function LegalPage({ kind = "mentions" }) {
  const nav = useNavigate();
  const page = LEGAL_CONTENT[kind] || LEGAL_CONTENT.mentions;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [kind]);

  // Liens internes du texte : navigation sans rechargement ; lien « Gérer les cookies » : ouvre les préférences
  const onArticleClick = (e) => {
    const a = e.target.closest("a");
    if (!a) return;
    if (a.hasAttribute("data-cookie-settings")) {
      e.preventDefault();
      openCookieSettings();
      return;
    }
    const href = a.getAttribute("href") || "";
    if (href.startsWith("/")) {
      e.preventDefault();
      nav(href);
    }
  };

  return (
    <div className="min-h-[60vh]">
      <Seo
        title={{ mentions: "Mentions légales", cgv: "Conditions générales de vente", confidentialite: "Politique de confidentialité" }[kind]}
        path={{ mentions: "/mentions-legales", cgv: "/cgv", confidentialite: "/confidentialite" }[kind]}
      />
      <style>{`
        .legal{color:#d4d4d8;font-size:15px;line-height:1.75}
        .legal .lead{color:#a1a1aa;font-size:16px;margin-bottom:8px}
        .legal h2{font-family:'Outfit',system-ui,sans-serif;font-weight:800;font-size:20px;color:#fafafa;margin:36px 0 10px;scroll-margin-top:90px;letter-spacing:-.01em}
        .legal p{margin:10px 0}
        .legal ul{margin:10px 0;padding-left:20px;list-style:disc}
        .legal li{margin:4px 0}
        .legal li::marker{color:#52525b}
        .legal b{color:#fafafa;font-weight:600}
        .legal a{color:#00BF63;text-decoration:underline;text-underline-offset:4px}
        .legal table{width:100%;border-collapse:collapse;margin:14px 0;font-size:14px}
        .legal th{text-align:left;color:#a1a1aa;font-weight:500;font-size:12px;text-transform:uppercase;letter-spacing:.1em;padding:8px 10px;border-bottom:1px solid rgba(255,255,255,.1)}
        .legal td{padding:10px;border-bottom:1px solid rgba(255,255,255,.05);vertical-align:top}
        .legal .legal-todo{background:rgba(245,158,11,.15);color:#fde68a;padding:0 4px;border-radius:4px}
      `}</style>

      <section className="border-b border-white/5">
        <div className="mx-auto max-w-5xl px-6 py-8 sm:py-14">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 sm:mb-3">Légal</div>
          <h1 className="font-display font-black text-[2rem] leading-tight sm:text-5xl">{page.title}</h1>
          <p className="mt-3 text-sm text-zinc-500">Dernière mise à jour : {LEGAL_UPDATED_AT}</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pt-6 pb-8 sm:py-10 grid gap-10 lg:grid-cols-[220px_1fr]">
        <nav className="hidden lg:block" aria-label="Sommaire">
          <div className="sticky top-24">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Sommaire</div>
            <ul className="space-y-2 text-sm">
              {page.sections.map(([title]) => (
                <li key={title}>
                  <a href={`#${slug(title)}`} className="text-zinc-400 hover:text-white">
                    {title}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-white/5 space-y-2 text-sm">
              {Object.entries(LEGAL_CONTENT)
                .filter(([k]) => k !== kind)
                .map(([k, v]) => (
                  <Link key={k} to={PATHS[k]} className="block text-zinc-500 hover:text-white">
                    {v.title}
                  </Link>
                ))}
            </div>
          </div>
        </nav>

        <article className="legal max-w-none" onClick={onArticleClick} data-testid={`legal-${kind}`}>
          <p className="lead">{page.intro}</p>
          {page.sections.map(([title, html]) => (
            <div key={title}>
              <h2 id={slug(title)}>{title}</h2>
              <div dangerouslySetInnerHTML={{ __html: safeHtml(html) }} />
            </div>
          ))}
        </article>
      </section>
    </div>
  );
}
